import { useEffect, useState } from 'react';

// A band that stays put while the page scrolls. "Always" keeps it in view; Shopify's "on scroll up" lets it
// slide out of the way while a shopper reads down and brings it back the moment they scroll up to look for
// it. Either way it wears a soft shadow once the page has moved, so it reads as lifted over the content.

/** How a band stays put, exactly as the section's Sticky setting offers it. */
export type StickyMode = 'none' | 'always' | 'scroll-up';

/** How far a shopper has to move before the band reacts, so a wobble of the thumb does not flicker it. */
const SLACK_PX = 8;

/** Whether the band is in view right now, and whether the page has scrolled under it. */
export type StickyState = { shown: boolean; scrolled: boolean };

/** A band at rest, as one object, so setting it again is no change and redraws nothing. */
const AT_REST: StickyState = { shown: true, scrolled: false };

// Follows the page's scroll for a sticky band: shown at the top and while scrolling up, lifted once scrolled.
export function useStickyBand(mode: StickyMode): StickyState {
  const [state, setState] = useState<StickyState>(AT_REST);

  useEffect(() => {
    if (mode === 'none') { setState(AT_REST); return; }
    let last = window.scrollY;
    let frame = 0;
    // 1. Read once a frame, and only act on a move past the slack.
    const read = () => {
      const now = window.scrollY;
      if (Math.abs(now - last) < SLACK_PX && now > 0) return;
      const shown = mode === 'always' || now < last || now <= 0;
      const scrolled = now > 0;
      // 2. An unchanged answer keeps the same object, so the band is not redrawn on every frame of a scroll.
      setState((was) => (was.shown === shown && was.scrolled === scrolled ? was : { shown, scrolled }));
      last = now;
    };
    const onScroll = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(read); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(frame); };
  }, [mode]);

  return state;
}
